import React, { FC } from "react"
import Skeleton from "react-loading-skeleton"

import { ProductCardLabel } from "scripts/components/react/ProductCard/ProductLabel"
import currencyFormat, { currencyFormatWithSign } from "scripts/utils/currencyFormat"
import { IProductCard } from "scripts/types/procuct"

interface IProductCardProps {
  product?: IProductCard
  isAuth: boolean
  onWishlistToggle?: (productId: number, isFavorite?: boolean) => void
}

const ProductCardBase: FC<IProductCardProps & { loading: boolean }> = ({
  product,
  isAuth,
  loading,
  onWishlistToggle,
}) => {
  if (loading || !product) {
    return (
      <div className="product-card product-card--skeleton">
        <div className="product-card__content">
          <div className="product-card__image flex-c-c">
            <div className="image" />
          </div>
          <div className="title">
            <Skeleton width={120} />
            <br />
            <Skeleton width={90} />
          </div>
          <div className="product-card__info">
            <div className="product-card__price-row" style={{ lineHeight: 0 }}>
              <div className="price">
                <Skeleton width={65} />
              </div>
              <div className="product-buttons">
                <Skeleton width={40} height={40} borderRadius={14} />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const {
    id,
    sku,
    slug,
    image,
    image_webp,
    name_without_brand,
    price,
    old_price,
    credit,
    creditFromPrice = 0,
    discount_percent,
    labels,
    showBonusBlock,
    bonuses: {
      price_with_discount,
      birthday_discount,
      cashback_price,
      discount_percent: bonus_discount_percent,
    },
    enabled,
    isFavorite,
    phrases,
  } = product

  const handleWishListBtnClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (onWishlistToggle) {
      onWishlistToggle(id, isFavorite)
    }
  }

  return (
    <div className={`product-card${!enabled ? " disabled" : ""}`}>
      <div className="product-card__content" data-id={id}>
        <a className="js_ga_select" href={slug} data-sku={sku}>
          <div className="product-card__image flex-c-c">
            {image_webp ? (
              <picture>
                <source type="image/webp" srcSet={image_webp} />
                <img
                  className="image"
                  src={image}
                  alt={name_without_brand}
                  loading="lazy"
                  width={244}
                  height={244}
                />
              </picture>
            ) : (
              <img
                className="image"
                src={image}
                alt={name_without_brand}
                loading="lazy"
                width={244}
                height={244}
              />
            )}
          </div>
          <div className="title">{name_without_brand}</div>
        </a>

        <div className="product-card__top">
          <div className="product-card__labels">
            {enabled ? (
              <>
                {labels.map((label, idx) => (
                  <ProductCardLabel key={idx} label={label} />
                ))}
                {labels.length > 0 && (
                  <div className="product__discount_bg product__label-popup hide js_product_label_info" />
                )}
              </>
            ) : (
              <div className="product-card__label product-card__label--disabled">
                {phrases.status}
              </div>
            )}
          </div>

          {enabled && isAuth && (
            <div
              className={`product-card__like js_favorite${isFavorite ? " active" : ""}`}
              onClick={handleWishListBtnClick}
            >
              <svg className="icon-favorite">
                <use href="#favorite" />
              </svg>
            </div>
          )}
          {enabled && !isAuth && (
            <div className="product-card__like modal-btn" data-target="modal-favorite">
              <svg className="icon-favorite">
                <use href="#favorite" />
              </svg>
            </div>
          )}
        </div>

        <div className="product-card__info">
          <div className="product-card__price-row">
            <div className="price">
              {enabled && old_price > price && (
                <div className="price__old-row">
                  <div className="price__old">{currencyFormatWithSign(old_price)}</div>
                  <div className="price__discount">
                    -{discount_percent}
                    <span>%</span>
                  </div>
                </div>
              )}
              <div className="price__current">{currencyFormatWithSign(price)}</div>
            </div>

            {enabled && (
              <div className="product-buttons">
                {showBonusBlock && (
                  <button className="product-buttons__item product-buttons__bonus js_toggle_discount" aria-label={phrases.discount_title}>
                    <svg className="icon-pay-discount">
                      <use href="#pay-discount" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>

          {enabled && (
            <a href={slug} className="wb-btn btn-green product-buttons__cart buy ">
              <svg className="icon-cart">
                <use href="#cart" />
              </svg>
              {phrases.buy_button}
            </a>
          )}

          {enabled && creditFromPrice > 0 && credit.length > 0 && (
            <div className="product-card__loans">
              {credit.map((item) => (
                <div className="credit__item" key={item.code}>
                  <svg className={`icon icon-b_${item.code}`}>
                    <use href={`#b_${item.code}`} />
                  </svg>
                </div>
              ))}
              <div
                className="credit__text"
                dangerouslySetInnerHTML={{
                  __html: phrases.buy_credit_from_to_small.replace(
                    ":count",
                    currencyFormat(creditFromPrice),
                  ),
                }}
              />
            </div>
          )}

          {isAuth && showBonusBlock && enabled && (
            <div className="product__discount_bg hide js_product_discount_info">
              <div className="prod__discount js_prod_bg tooltip__outer">
                <svg className="product__discount_close js_close_discount">
                  <use href="#close" />
                </svg>
                <div className="product__discount_title">{phrases.discount_title}</div>
                <div className="product__discount_li">
                  <svg className="icon-percent">
                    <use href="#pay-discount" />
                  </svg>
                  <div>{phrases.discount}</div>
                  <div className="product__discount_val">{bonus_discount_percent}%</div>
                </div>
                <div className="product__discount_li">
                  <svg className="icon-calendar">
                    <use href="#calendar" />
                  </svg>
                  <div>{phrases.bd}</div>
                  <div className="product__discount_val">{birthday_discount}%</div>
                </div>
                <div className="product__discount_li">
                  <svg className="icon-pay-bonus">
                    <use href="#pay-bonus" />
                  </svg>
                  <div>{phrases.cb}</div>
                  <div className="product__discount_val">{cashback_price}</div>
                </div>
                <div className="product__discount_footer">
                  <span>{phrases.price}</span>
                  <span className="product__discount_price">{price_with_discount}</span>
                </div>
              </div>
            </div>
          )}

          {!isAuth && showBonusBlock && enabled && (
            <div className="product__discount_bg hide js_product_discount_info">
              <div className="prod__discount js_prod_bg tooltip__outer">
                <svg className="product__discount_close js_close_discount">
                  <use href="#close" />
                </svg>
                <div className="product__discount_title">{phrases.get_discount}</div>
                <div className="product__discount_subtitle">{phrases.reg_text}</div>
                <div className="product__discount_li">
                  <svg className="icon-percent">
                    <use href="#pay-discount" />
                  </svg>
                  <div>{phrases.discount}</div>
                  <div className="product__discount_val">{bonus_discount_percent}%</div>
                </div>
                <div className="product__discount_li">
                  <svg className="icon-calendar">
                    <use href="#calendar" />
                  </svg>
                  <div>{phrases.bd}</div>
                  <div className="product__discount_val">{birthday_discount}%</div>
                </div>
                <div className="product__discount_li">
                  <svg className="icon-pay-bonus">
                    <use href="#pay-bonus" />
                  </svg>
                  <div>{phrases.cb}</div>
                  <div className="product__discount_val">{cashback_price}</div>
                </div>
                <div className="product__discount_mt">
                  <a
                    className="product__discount_footer_link modal-btn"
                    data-ga="listing"
                    data-target="modal-login"
                    href=""
                  >
                    {phrases.button_text}
                    <svg
                      className="product__register_icon"
                      width="22"
                      height="14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0 7h21.5m0 0L15 .5M21.5 7L15 13.5"
                        stroke="#00502F"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const ProductCard: FC<IProductCardProps> = (props) => (
  <ProductCardBase loading={false} {...props} />
)

export const ProductCardSkeleton: FC<IProductCardProps> = (props) => (
  <ProductCardBase loading={true} {...props} />
)
